import React, { useEffect, useState, useCallback } from 'react';
import { useScenario } from '../context/ScenarioContext';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';
import { CrudSection } from '../components/CrudSection';
import { MixOverview } from '../components/MixOverview';
import { PageHeader, LoadingTable, ErrorState } from '../components/States';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { AlertTriangle } from 'lucide-react';
import { InlinePctCell } from '../components/InlinePctCell';
import { formatPercent, formatNumber, formatEurPerDay, formatEurPerWp, formatCurrency } from '../lib/format';

export default function MixInstallatori() {
  const { active, sid, loading, error, reload } = useScenario();
  const { canWriteList } = useAuth();
  const canWrite = canWriteList('product_mix');
  const [kpi, setKpi] = useState(null);

  const loadKpi = useCallback(() => {
    if (sid) api.getKpi(sid).then(setKpi).catch(() => setKpi(null));
  }, [sid]);
  useEffect(() => { loadKpi(); }, [loadKpi]);

  const reloadAll = useCallback(() => { reload(); loadKpi(); }, [reload, loadKpi]);

  if (loading) return (<div><PageHeader title="Mix Installatori" /><LoadingTable /></div>);
  if (error || !active) return (<div><PageHeader title="Mix Installatori" /><ErrorState onRetry={reload} /></div>);

  const rows = active.product_mix || [];
  const totShare = rows.reduce((s, r) => s + (r.share_pct || 0), 0);
  const dailyById = Object.fromEntries((kpi?.product_mix_details || []).map((d) => [d.id, d.daily_sales]));
  const priceById = Object.fromEntries((kpi?.product_mix_details || []).map((d) => [d.id, d.price_wp]));
  const revenueById = Object.fromEntries((kpi?.product_mix_details || []).map((d) => [d.id, d.revenue_derived]));
  const kwpById = Object.fromEntries((kpi?.product_mix_details || []).map((d) => [d.id, (d.is_panel && d.price_wp) ? (d.revenue_derived / d.price_wp) / 1000 : null]));
  const PLANT_SHORT = { italia: 'Italia', extra_ue: 'Extra UE' };
  const columns = [
    { key: 'name', header: 'Categoria prodotto', render: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'share_pct', header: 'Quota', align: 'right', sortValue: (r) => r.share_pct || 0, render: (r) => <InlinePctCell row={r} field="share_pct" canWrite={canWrite} min={0} testid={`mix-quota-${r.id}`} onSave={(v) => api.updateMix(sid, 'product_mix', r.id, { ...r, share_pct: v }).then(reloadAll)} /> },
    { key: 'industrial_margin_pct', header: 'Margine su ricavi', align: 'right', sortValue: (r) => r.industrial_margin_pct || 0, render: (r) => <InlinePctCell row={r} field="industrial_margin_pct" canWrite={canWrite} testid={`mix-margin-${r.id}`} onSave={(v) => api.updateMix(sid, 'product_mix', r.id, { ...r, industrial_margin_pct: v }).then(reloadAll)} /> },
    { key: 'volume_eur', header: 'Volume (€)', align: 'right', sortable: false, render: (r) => formatCurrency(revenueById[r.id]) },
    { key: 'volume_kwp', header: 'Volume (kWp)', align: 'right', sortable: false, render: (r) => kwpById[r.id] != null ? `${formatNumber(kwpById[r.id], 0)} kWp` : <span className="text-muted-foreground">—</span> },
    { key: 'price_wp', header: 'Prezzo €/W', align: 'right', sortable: false, render: (r) => (r.is_panel && priceById[r.id]) ? formatEurPerWp(priceById[r.id]) : <span className="text-muted-foreground">—</span> },
    { key: 'daily', header: 'Vendita/giorno', align: 'right', sortable: false, render: (r) => formatEurPerDay(dailyById[r.id]) },
    { key: 'is_panel', header: 'Tipo', render: (r) => <Badge variant={r.is_panel ? 'default' : 'secondary'}>{r.is_panel ? 'Pannello FV' : 'Cross-selling'}</Badge>, sortValue: (r) => (r.is_panel ? 1 : 0) },
    { key: 'plant', header: 'Stabilimento', sortable: false, render: (r) => r.is_panel
        ? (r.plant ? <Badge variant="outline">{PLANT_SHORT[r.plant] || r.plant}</Badge> : <span className="inline-flex items-center gap-1 text-[hsl(var(--warning))]"><AlertTriangle className="h-3.5 w-3.5" />Da assegnare</span>)
        : <span className="text-muted-foreground">—</span> },
  ];
  const fields = [
    { name: 'name', label: 'Categoria prodotto', type: 'text' },
    { name: 'share_pct', label: 'Quota (%)', type: 'percent' },
    { name: 'industrial_margin_pct', label: 'Margine su ricavi (%)', type: 'percent' },
    { name: 'is_panel', label: 'Pannello fotovoltaico (usa €/Wp)', type: 'checkbox' },
    { name: 'plant', label: 'Stabilimento di produzione', type: 'select',
      options: [{ value: 'italia', label: 'Stabilimento italiano' }, { value: 'extra_ue', label: 'Stabilimento extra UE' }],
      showIf: (v) => !!v.is_panel, requiredIf: (v) => !!v.is_panel,
      help: 'Obbligatorio per i pannelli FV: indica dove viene prodotto il pannello.' },
    { name: 'note', label: 'Note', type: 'textarea' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Mix Installatori" description="Composizione prodotti della Divisione Installatori" />
      {Math.abs(totShare - 1) > 0.005 && (
        <Card className="p-3 flex items-center gap-2 text-sm border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10"><AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" />Le quote sommano a {formatNumber(totShare * 100, 1)}% (atteso 100%).</Card>
      )}
      {kpi && <MixOverview division="Installatori" rows={rows} kpi={kpi} assumptions={active.assumptions} sid={sid} canWrite={canWrite} onReload={reloadAll} />}
      <CrudSection rows={rows} columns={columns} fields={fields} canWrite={canWrite} addLabel="Nuova categoria"
        onAdd={(p) => api.addMix(sid, 'product_mix', p)} onUpdate={(id, p) => api.updateMix(sid, 'product_mix', id, p)} onDelete={(id) => api.deleteMix(sid, 'product_mix', id)} afterChange={reloadAll} describeRow={(r) => `Eliminare "${r.name}"?`} />
    </div>
  );
}
